import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import PublicStoreSection from "@/components/PublicStoreSection";
import axios from "axios";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Loader2, Calendar as CalendarIcon, Clock, User as UserIcon, CheckCircle2, ChevronRight, ChevronLeft, Sparkles, Search } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function fmtApiError(err, fallback = "Operação falhou.") {
  const d = err?.response?.data?.detail;
  if (!d) return fallback;
  if (typeof d === "string") return d;
  if (typeof d === "object" && d.message) return d.message;
  if (Array.isArray(d)) {
    return d.map((e) => e?.msg).filter(Boolean).join("; ") || fallback;
  }
  return fallback;
}

function fmtPrice(v) {
  return (v || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
}

function todayPlus(daysOffset = 0) {
  const d = new Date();
  d.setDate(d.getDate() + daysOffset);
  return d.toISOString().slice(0, 10);
}

export default function PublicBooking() {
  const { slug } = useParams();
  const navigate = useNavigate();

  const [step, setStep] = useState(1); // 1=serviço, 2=profissional, 3=data/hora, 4=dados, 5=sucesso
  const [tenant, setTenant] = useState(null);
  const [services, setServices] = useState([]);
  const [resources, setResources] = useState([]);
  const [slots, setSlots] = useState([]);
  const [slotsInfo, setSlotsInfo] = useState({ closed: false, reason: "" });

  const [serviceId, setServiceId] = useState("");
  const [resourceId, setResourceId] = useState(""); // "" = qualquer
  const [date, setDate] = useState(todayPlus(0));
  const [startAt, setStartAt] = useState("");
  const [form, setForm] = useState({ client_name: "", client_cpf: "", client_email: "", client_phone: "", notes: "" });

  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const service = useMemo(() => services.find((s) => s.id === serviceId), [services, serviceId]);
  const resource = useMemo(() => resources.find((r) => r.id === resourceId), [resources, resourceId]);

  // Load tenant + services + resources
  useEffect(() => {
    (async () => {
      try {
        const [t, s, r] = await Promise.all([
          axios.get(`${API}/public/booking/${slug}/info`),
          axios.get(`${API}/public/booking/${slug}/services`),
          axios.get(`${API}/public/booking/${slug}/resources`),
        ]);
        setTenant(t.data);
        setServices(s.data.items || []);
        setResources(r.data.items || []);
        // Apply primary color
        if (t.data.primary_color) {
          document.documentElement.style.setProperty("--tenant-primary", t.data.primary_color);
        }
      } catch (e) {
        setError(e?.response?.data?.detail || "Empresa não encontrada ou auto-agendamento desativado.");
      } finally {
        setLoading(false);
      }
    })();
  }, [slug]);

  // Load slots when service/resource/date changes & step 3
  useEffect(() => {
    if (step !== 3 || !serviceId) return;
    (async () => {
      try {
        const params = { date, service_id: serviceId };
        if (resourceId) params.resource_id = resourceId;
        const { data } = await axios.get(`${API}/public/booking/${slug}/slots`, { params });
        setSlots(data.slots || []);
        setSlotsInfo({ closed: !!data.closed, reason: data.reason || "" });
      } catch (e) {
        setSlots([]);
      }
    })();
  }, [step, serviceId, resourceId, date, slug]);

  const goNext = () => setStep((s) => Math.min(5, s + 1));
  const goPrev = () => setStep((s) => Math.max(1, s - 1));

  const confirmBooking = async () => {
    setBusy(true);
    try {
      const payload = {
        service_id: serviceId,
        resource_id: resourceId || null,
        start_at: startAt,
        ...form,
        client_cpf: (form.client_cpf || "").replace(/\D/g, ""),
      };
      const { data } = await axios.post(`${API}/public/booking/${slug}/book`, payload);
      setResult(data);
      setStep(5);
    } catch (e) {
      toast.error(fmtApiError(e, "Não foi possível agendar."));
    } finally {
      setBusy(false);
    }
  };

  const resetWizard = () => {
    setStep(1);
    setServiceId("");
    setResourceId("");
    setStartAt("");
    setForm({ client_name: "", client_cpf: "", client_email: "", client_phone: "", notes: "" });
    setResult(null);
    setDate(todayPlus(0));
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>;
  }
  if (error || !tenant) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <Card className="p-8 max-w-md text-center rounded-2xl">
          <div className="text-2xl font-display font-semibold mb-2">Indisponível</div>
          <p className="text-sm text-muted-foreground">{error || "Não foi possível abrir esta página."}</p>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-muted/30">
      <header className="border-b border-border bg-background/60 backdrop-blur sticky top-0 z-20">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center gap-3">
          {tenant.logo_url ? (
            <img src={tenant.logo_url} alt="logo" className="h-9 w-9 rounded-md object-cover" />
          ) : (
            <div className="h-9 w-9 rounded-md bg-tenant/10 flex items-center justify-center">
              <Sparkles className="w-5 h-5 text-tenant" />
            </div>
          )}
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Agende online</div>
            <div className="font-display font-semibold truncate">{tenant.name}</div>
          </div>
        </div>
        <div className="max-w-3xl mx-auto px-4 pb-3">
          {(tenant.landing_display_mode || "hybrid") !== "store_only" && <StepperBar step={step} />}
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-6 space-y-6">
        {(() => {
          const mode = tenant.landing_display_mode || "hybrid";
          // store_only: só vitrine, sem stepper nem fluxo de agenda
          if (mode === "store_only") {
            return <PublicStoreSection slug={slug} />;
          }
          // Booking flow (hybrid + booking_only) renderiza normal.
          // Vitrine aparece apenas no step 1 em modo hybrid.
          return <>
            {step === 1 && (
              <>
                {mode === "hybrid" && <PublicStoreSection slug={slug} />}
                <ServiceStep
                  services={services}
                  selected={serviceId}
                  onSelect={(id) => { setServiceId(id); goNext(); }}
                />
              </>
            )}

        {step === 2 && (
          <ResourceStep
            resources={resources}
            selected={resourceId}
            onSelect={(id) => { setResourceId(id || ""); goNext(); }}
            onBack={goPrev}
          />
        )}

        {step === 3 && service && (
          <SlotsStep
            date={date}
            onDateChange={setDate}
            slots={slots}
            closed={slotsInfo.closed}
            reason={slotsInfo.reason}
            selected={startAt}
            onSelect={(iso) => { setStartAt(iso); goNext(); }}
            onBack={goPrev}
            service={service}
            resource={resource}
          />
        )}

        {step === 4 && (
          <DataStep
            form={form}
            setForm={setForm}
            onBack={goPrev}
            onSubmit={confirmBooking}
            busy={busy}
            service={service}
            resource={resource}
            startAt={startAt}
          />
        )}

        {step === 5 && result && (
          <SuccessStep
            result={result}
            onClickHome={resetWizard}
            tenant={tenant}
          />
        )}
          </>;
        })()}
      </main>

      <footer className="text-center text-xs text-muted-foreground py-6">
        Powered by <span className="font-semibold">SUA PLATAFORMA</span>
      </footer>
    </div>
  );
}

function StepperBar({ step }) {
  const total = 4;
  return (
    <div className="flex items-center gap-1.5">
      {Array.from({ length: total }).map((_, i) => (
        <div key={i} className="flex-1 h-1.5 rounded-full bg-muted overflow-hidden">
          <div
            className="h-full bg-tenant transition-all duration-300"
            style={{ width: step > i ? "100%" : step === i + 1 ? "50%" : "0%", background: "hsl(var(--tenant-primary))" }}
          />
        </div>
      ))}
    </div>
  );
}

function ServiceStep({ services, selected, onSelect }) {
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 10;

  const filtered = useMemo(() => {
    if (!q) return services;
    const s = q.toLowerCase().trim();
    return services.filter((sv) => String(sv.name || "").toLowerCase().includes(s));
  }, [services, q]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const pageItems = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  const onQChange = (v) => { setQ(v); setPage(1); };

  return (
    <section data-testid="booking-step-service">
      <h1 className="font-display text-2xl font-semibold mb-1">Escolha o serviço</h1>
      <p className="text-sm text-muted-foreground mb-4">Selecione o que você quer agendar.</p>
      {services.length === 0 ? (
        <Card className="p-8 text-center text-muted-foreground">Nenhum serviço disponível.</Card>
      ) : (
        <>
          <div className="mb-4 relative max-w-md">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
            <Input
              placeholder="Buscar serviço pelo nome…"
              value={q}
              onChange={(e) => onQChange(e.target.value)}
              className="pl-9"
              data-testid="booking-service-search"
            />
          </div>
          {filtered.length === 0 ? (
            <Card className="p-8 text-center text-muted-foreground">
              Nenhum serviço encontrado para &quot;{q}&quot;.
            </Card>
          ) : (
            <>
              <div className="grid gap-3 sm:grid-cols-2">
                {pageItems.map((s) => (
                  <button
                    key={s.id}
                    data-testid={`booking-service-${s.id}`}
                    onClick={() => onSelect(s.id)}
                    className={`text-left p-4 rounded-xl border-2 transition hover:border-tenant hover:shadow-md ${selected === s.id ? "border-tenant" : "border-border"}`}
                    style={{ borderColor: selected === s.id ? "hsl(var(--tenant-primary))" : undefined }}
                  >
                    <div className="font-medium">{s.name}</div>
                    <div className="text-xs text-muted-foreground mt-1">
                      <Clock className="w-3 h-3 inline mr-1" /> {s.duration_minutes} min · {fmtPrice(s.price)}
                    </div>
                  </button>
                ))}
              </div>
              {totalPages > 1 && (
                <div className="flex items-center justify-between gap-3 mt-5" data-testid="booking-service-pagination">
                  <div className="text-xs text-muted-foreground">
                    Página <strong>{safePage}</strong> de <strong>{totalPages}</strong> · {filtered.length} serviços
                  </div>
                  <div className="flex items-center gap-2">
                    <Button size="sm" variant="outline" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={safePage <= 1} data-testid="booking-service-pagination-prev">
                      <ChevronLeft className="w-3.5 h-3.5" /> Anterior
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={safePage >= totalPages} data-testid="booking-service-pagination-next">
                      Próxima <ChevronRight className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </>
      )}
    </section>
  );
}

function ResourceStep({ resources, selected, onSelect, onBack }) {
  return (
    <section data-testid="booking-step-resource">
      <h1 className="font-display text-2xl font-semibold mb-1">Escolha o profissional</h1>
      <p className="text-sm text-muted-foreground mb-4">Ou deixe em &quot;Sem preferência&quot; e qualquer profissional atende.</p>
      <div className="grid gap-3 sm:grid-cols-2">
        <button
          data-testid="booking-resource-none"
          onClick={() => onSelect("")}
          className={`text-left p-4 rounded-xl border-2 transition hover:shadow-md ${!selected ? "border-tenant" : "border-border"}`}
          style={{ borderColor: !selected ? "hsl(var(--tenant-primary))" : undefined }}
        >
          <div className="font-medium">Sem preferência</div>
          <div className="text-xs text-muted-foreground mt-1">Qualquer profissional disponível.</div>
        </button>
        {resources.map((r) => (
          <button
            key={r.id}
            data-testid={`booking-resource-${r.id}`}
            onClick={() => onSelect(r.id)}
            className={`text-left p-4 rounded-xl border-2 transition hover:shadow-md flex items-center gap-3 ${selected === r.id ? "border-tenant" : "border-border"}`}
            style={{ borderColor: selected === r.id ? "hsl(var(--tenant-primary))" : undefined }}
          >
            {r.photo_url ? (
              <img src={r.photo_url} alt={r.name} className="w-10 h-10 rounded-full object-cover" />
            ) : (
              <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center">
                <UserIcon className="w-4 h-4 text-muted-foreground" />
              </div>
            )}
            <div className="min-w-0">
              <div className="font-medium truncate">{r.name}</div>
              {r.bio && <div className="text-xs text-muted-foreground truncate">{r.bio}</div>}
            </div>
          </button>
        ))}
      </div>
      <div className="mt-6">
        <Button variant="ghost" onClick={onBack} data-testid="booking-resource-back"><ChevronLeft className="w-4 h-4 mr-1" /> Voltar</Button>
      </div>
    </section>
  );
}

function SlotsStep({ date, onDateChange, slots, closed, reason, selected, onSelect, onBack, service, resource }) {
  const minDate = todayPlus(0);
  const maxDate = todayPlus(60);
  return (
    <section data-testid="booking-step-slots">
      <h1 className="font-display text-2xl font-semibold mb-1">Escolha data e horário</h1>
      <p className="text-sm text-muted-foreground mb-4">
        {service.name} · {service.duration_minutes} min{resource ? ` · ${resource.name}` : ""}
      </p>
      <div className="mb-4 max-w-xs">
        <Label className="text-xs uppercase tracking-wider">Dia</Label>
        <Input
          data-testid="booking-date"
          type="date"
          value={date}
          min={minDate}
          max={maxDate}
          onChange={(e) => onDateChange(e.target.value)}
        />
      </div>
      {closed ? (
        <Card className="p-6 text-center text-muted-foreground" data-testid="booking-closed">
          <CalendarIcon className="w-5 h-5 mx-auto mb-2" />
          {reason || "Empresa fechada neste dia."}
        </Card>
      ) : (
        <div className="grid grid-cols-4 sm:grid-cols-6 gap-2" data-testid="booking-slots">
          {slots.length === 0 && (
            <div className="col-span-full text-center text-sm text-muted-foreground py-8">Sem horários disponíveis para este dia.</div>
          )}
          {slots.map((s) => (
            <button
              key={s.start_at}
              disabled={!s.available}
              onClick={() => onSelect(s.start_at)}
              data-testid={`booking-slot-${s.label}`}
              className={`px-2 py-2 rounded-lg text-sm border-2 transition font-mono
                ${!s.available ? "opacity-40 cursor-not-allowed border-border bg-muted/50" :
                  selected === s.start_at ? "border-tenant" : "border-border hover:border-tenant"}`}
              style={selected === s.start_at ? { borderColor: "hsl(var(--tenant-primary))" } : undefined}
            >
              {s.label}
            </button>
          ))}
        </div>
      )}
      <div className="mt-6">
        <Button variant="ghost" onClick={onBack} data-testid="booking-slots-back"><ChevronLeft className="w-4 h-4 mr-1" /> Voltar</Button>
      </div>
    </section>
  );
}

function DataStep({ form, setForm, onBack, onSubmit, busy, service, resource, startAt }) {
  const cpfDigits = (form.client_cpf || "").replace(/\D/g, "");
  const canSubmit = (form.client_name || "").trim().length >= 2 && cpfDigits.length === 11;
  const fmtCpf = (v) => {
    const d = (v || "").replace(/\D/g, "").slice(0, 11);
    if (d.length <= 3) return d;
    if (d.length <= 6) return `${d.slice(0, 3)}.${d.slice(3)}`;
    if (d.length <= 9) return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6)}`;
    return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6, 9)}-${d.slice(9, 11)}`;
  };
  return (
    <section data-testid="booking-step-data">
      <h1 className="font-display text-2xl font-semibold mb-1">Seus dados</h1>
      <p className="text-sm text-muted-foreground mb-4">CPF é obrigatório — usamos para identificar você e dar acesso ao seu portal.</p>

      <Card className="p-4 rounded-xl border-border mb-4 bg-muted/30">
        <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground mb-1">Resumo</div>
        <div className="font-medium">{service.name}{resource ? ` · ${resource.name}` : ""}</div>
        <div className="text-sm text-muted-foreground">
          {new Date(startAt).toLocaleString("pt-BR", { dateStyle: "full", timeStyle: "short" })}
        </div>
        <div className="text-sm">{fmtPrice(service.price)}</div>
      </Card>

      <div className="space-y-4">
        <div>
          <Label>Nome completo *</Label>
          <Input data-testid="booking-name" value={form.client_name} onChange={(e) => setForm({ ...form, client_name: e.target.value })} required />
        </div>
        <div>
          <Label>CPF *</Label>
          <Input
            data-testid="booking-cpf"
            inputMode="numeric"
            placeholder="000.000.000-00"
            value={form.client_cpf}
            onChange={(e) => setForm({ ...form, client_cpf: fmtCpf(e.target.value) })}
            className="font-mono"
          />
          <p className="text-[10px] text-muted-foreground mt-1">
            Se você já é cliente, vamos reconhecer pelo CPF. Senão, criaremos seu cadastro automaticamente.
          </p>
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <Label>Telefone</Label>
            <Input data-testid="booking-phone" value={form.client_phone} onChange={(e) => setForm({ ...form, client_phone: e.target.value })} placeholder="(11) 99999-0000" />
          </div>
          <div>
            <Label>E-mail (opcional)</Label>
            <Input data-testid="booking-email" type="email" value={form.client_email} onChange={(e) => setForm({ ...form, client_email: e.target.value })} />
          </div>
        </div>
        <div>
          <Label>Observações (opcional)</Label>
          <Textarea data-testid="booking-notes" rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
        </div>
        <div className="flex justify-between pt-2">
          <Button variant="ghost" onClick={onBack} data-testid="booking-data-back"><ChevronLeft className="w-4 h-4 mr-1" /> Voltar</Button>
          <Button
            data-testid="booking-submit"
            disabled={!canSubmit || busy}
            onClick={onSubmit}
            className="gap-2"
            style={{ background: "hsl(var(--tenant-primary))", color: "hsl(var(--tenant-primary-foreground))" }}
          >
            {busy && <Loader2 className="w-4 h-4 animate-spin" />} Confirmar agendamento <ChevronRight className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </section>
  );
}

function SuccessStep({ result, onClickHome, tenant }) {
  return (
    <section className="text-center py-6" data-testid="booking-step-success">
      <div className="w-16 h-16 mx-auto rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center mb-4">
        <CheckCircle2 className="w-8 h-8 text-emerald-600" />
      </div>
      <h1 className="font-display text-3xl font-bold tracking-tight mb-2">Agendamento confirmado!</h1>
      <p className="text-sm text-muted-foreground mb-6">
        Te esperamos no <strong>{tenant.name}</strong>.
      </p>
      {result.is_new_client && (
        <div data-testid="booking-new-client-badge" className="inline-block mb-4 px-3 py-1.5 rounded-full bg-teal-50 dark:bg-teal-900/30 text-teal-700 text-xs font-semibold border border-teal-300">
          ✨ Bem-vindo! Você foi cadastrado como novo cliente.
        </div>
      )}
      <Card className="p-5 max-w-md mx-auto text-left rounded-xl">
        <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground mb-1">Detalhes</div>
        <div className="font-medium">{result.service_name}{result.resource_name ? ` · ${result.resource_name}` : ""}</div>
        <div className="text-sm text-muted-foreground mt-1">
          {new Date(result.start_at).toLocaleString("pt-BR", { dateStyle: "full", timeStyle: "short" })}
        </div>
      </Card>
      <Button variant="outline" onClick={onClickHome} className="mt-6" data-testid="booking-new">
        Agendar mais um horário
      </Button>
    </section>
  );
}
